import { useState } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import AuthShell from "../components/AuthShell";
import whitelogo from "../assets/whiteintel-white-logo-new.png";

const RECOVER_URL = "https://whiteintel.io/forgot-password-request_handler.php";
const RESET_URL = "https://whiteintel.io/reset-password_handler.php";

export default function Recover() {
  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get("resetToken");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reTypePassword, setReTypePassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  const handleRecover = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess(false);
    setLoading(true);
    try {
      const res = await fetch(RECOVER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Recovery failed");
      setSuccess(true);
    } catch (e) {
      setErr(e.message || "Recovery failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess(false);

    if (password !== reTypePassword) {
      setErr("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(RESET_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          resetToken,
          password,
          reTypePassword,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Reset failed");

      setSuccess(true);
    } catch (e) {
      setErr(e.message || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="w-full max-w-[420px] rounded-2xl border border-indigo-500/15 bg-gradient-to-b from-slate-900/85 to-[#080d1c]/95 backdrop-blur-xl shadow-[0_0_70px_-10px_rgba(99,102,241,0.22),0_28px_70px_-26px_rgba(0,0,0,0.9)]">
        <div className="px-7 pt-7 pb-6">
          <div className="flex items-center gap-3">
            <img src={whitelogo} alt="WhiteIntel" className="h-8 w-auto shrink-0" />
            <h1 className="text-[19px] font-semibold text-white leading-snug">
              {resetToken ? "Set a new password" : "Reset your password"}
            </h1>
          </div>
          <p className="mt-3 text-[13px] text-slate-500 leading-relaxed">
            {resetToken
              ? "Choose a strong, unique passphrase. You'll use it next time you sign in."
              : "Enter your email and we'll send a secure link to reset your password."}
          </p>

          {err && (
            <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-[13px] text-red-300">
              <div className="flex items-start justify-between gap-3">
                <span className="pt-0.5">{err}</span>
                <button
                  type="button"
                  onClick={() => setErr("")}
                  className="shrink-0 p-1 rounded hover:bg-red-500/20 text-red-300"
                  aria-label="Dismiss error"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          {success && !resetToken && (
            <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[13px] text-emerald-300">
              Password reset link has been sent to your email.
            </div>
          )}
          {success && resetToken && (
            <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[13px] text-emerald-300">
              Your password has been reset.{" "}
              <a href="/login" className="underline text-indigo-300 hover:text-indigo-200">
                Sign in
              </a>
            </div>
          )}

          {!resetToken && !success && (
            <form onSubmit={handleRecover} className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] px-3 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                  autoComplete="email"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-full rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 text-[14px] font-semibold text-white hover:from-blue-500 hover:to-indigo-500 shadow-lg shadow-indigo-900/40 disabled:opacity-50 transition-all"
              >
                {loading ? "Sending…" : "Send recovery link"}
              </button>
            </form>
          )}

          {resetToken && !success && (
            <form onSubmit={handleReset} className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400">New password</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] pl-3 pr-9 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-300"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400">Confirm new password</label>
                <input
                  type={showPw ? "text" : "password"}
                  value={reTypePassword}
                  onChange={(e) => setReTypePassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] px-3 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                  autoComplete="new-password"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-full rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 text-[14px] font-semibold text-white hover:from-blue-500 hover:to-indigo-500 shadow-lg shadow-indigo-900/40 disabled:opacity-50 transition-all"
              >
                {loading ? "Resetting…" : "Reset password"}
              </button>
            </form>
          )}
        </div>

        <div className="rounded-b-2xl border-t border-indigo-500/15 bg-[#05091a]/60 px-7 py-3 text-[11.5px] text-slate-500">
          Back to{" "}
          <a href="/login" className="text-indigo-300 hover:text-indigo-200">
            Sign in
          </a>
        </div>
      </div>
    </AuthShell>
  );
}
